/**
 * Zentrale Token-Verwaltung fuer den Viewer im Draft-Modus.
 *
 * Der Viewer erhaelt den initialen Editor-Token per URL-Query
 * (`?mode=draft&token=...`) vom Editor. Da die Default-TTL eines Editor-Tokens
 * begrenzt ist (~60min), wuerden laengere Preview-Sessions sonst stillschweigend
 * ablaufen: `/scene/draft` liefert 401, der WebSocket schliesst sich.
 *
 * Dieses Modul:
 * - haelt den aktuellen Token in einem Memory-Slot (Single Source of Truth),
 * - decodiert den `exp`-Claim aus dem Payload,
 * - holt rechtzeitig vor Ablauf einen frischen Token via
 *   `POST {cms}/api/editor/refresh-token`,
 * - benachrichtigt Subscriber (z.B. live-sync), damit WebSocket-Re-Auths greifen,
 * - aktualisiert die Browser-URL via `history.replaceState`, sodass Reloads
 *   und die `?token=...` der bestehenden HTTP-Calls den frischen Token sehen.
 *
 * CORS: Refresh-Endpoint nimmt den Token ueber `Authorization: Bearer`,
 * keine Cookies, kein `credentials: 'include'` noetig.
 */

import {
  getConfiguredCmsOrigin,
  shouldUseCmsDevProxy,
} from "./cms-origin";

const REFRESH_BUFFER_SECONDS = 5 * 60;
const MIN_REFRESH_INTERVAL_MS = 30_000;
const RETRY_BACKOFFS_MS = [15_000, 60_000, 5 * 60_000];
const CMS_PROXY_PREFIX = "/__cms";

function base64UrlDecode(value) {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? padded : padded + "=".repeat(4 - (padded.length % 4));
  return atob(pad);
}

/**
 * Liest den `exp`-Claim aus einem Editor-Token.
 * @param {string|null|undefined} token
 * @returns {number|null} Unix-Timestamp in Sekunden oder null.
 */
export function decodeEditorTokenExp(token) {
  if (!token || typeof token !== "string") return null;
  const segments = token.split(".");
  if (segments.length < 2) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(segments[0]));
    return typeof payload?.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Liest mode/token aus der URL (einmalig beim Boot).
 */
function readInitial() {
  if (typeof window === "undefined") {
    return { mode: "public", token: null };
  }
  const params = new URLSearchParams(window.location.search);
  const rawMode = params.get("mode");
  return {
    mode: rawMode === "draft" ? "draft" : "public",
    token: params.get("token"),
  };
}

/**
 * Basis-URL fuer den CMS-Refresh-Endpoint. Im Vite-Dev-Modus wird der
 * `/__cms`-Proxy genutzt, sodass Cookies/CORS bei LAN-/localhost-Setups einfach
 * bleiben. Sonst `VITE_CMS_ORIGIN` (Default: http://localhost:3001).
 */
function refreshEndpoint() {
  if (shouldUseCmsDevProxy()) {
    return `${CMS_PROXY_PREFIX}/api/editor/refresh-token`;
  }
  return `${getConfiguredCmsOrigin()}/api/editor/refresh-token`;
}

const initial = readInitial();

let currentToken = initial.token;
let currentExp = decodeEditorTokenExp(initial.token);
let mode = initial.mode;
let refreshTimer = null;
let inFlight = null;
let lastRefreshAt = 0;
let consecutiveFailures = 0;
let disposed = false;

const subscribers = new Set();

function clearTimer() {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function notify(reason) {
  for (const subscriber of subscribers) {
    try {
      subscriber(currentToken, reason);
    } catch (error) {
      console.warn("[session-token] Subscriber threw:", error);
    }
  }
}

function updateUrlToken(newToken) {
  if (typeof window === "undefined" || !newToken) return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("token") === newToken) return;
    url.searchParams.set("token", newToken);
    // replaceState (nicht pushState), damit der Back-Button nicht durch
    // jeden Token-Refresh zu einem History-Eintrag wird.
    window.history.replaceState(window.history.state, "", url.toString());
  } catch (error) {
    console.warn("[session-token] Failed to update URL token:", error);
  }
}

function computeNextDelayMs() {
  if (!currentExp) return null;
  const now = Date.now() / 1000;
  const secondsUntilRefresh = currentExp - REFRESH_BUFFER_SECONDS - now;
  const ms = secondsUntilRefresh * 1000;
  if (ms < MIN_REFRESH_INTERVAL_MS) return MIN_REFRESH_INTERVAL_MS;
  return Math.min(ms, 2 ** 31 - 1);
}

function scheduleNext() {
  if (disposed) return;
  clearTimer();
  const delay = computeNextDelayMs();
  if (delay === null) return;
  refreshTimer = setTimeout(() => {
    void runRefresh();
  }, delay);
}

async function runRefresh() {
  if (disposed) return null;
  if (inFlight) return inFlight;
  if (!currentToken) return null;
  if (mode !== "draft") return null;

  if (Date.now() - lastRefreshAt < MIN_REFRESH_INTERVAL_MS && consecutiveFailures === 0) {
    scheduleNext();
    return null;
  }

  inFlight = (async () => {
    try {
      const response = await fetch(refreshEndpoint(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${currentToken}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: HTTP ${response.status}`);
      }

      const data = await response.json();
      if (!data?.token) {
        throw new Error("Token refresh failed: missing token in response");
      }

      currentToken = data.token;
      currentExp = decodeEditorTokenExp(data.token);
      lastRefreshAt = Date.now();
      consecutiveFailures = 0;

      updateUrlToken(currentToken);
      notify("refresh");
      scheduleNext();
      console.log(
        "[session-token] Refreshed; next exp at",
        currentExp ? new Date(currentExp * 1000).toISOString() : "(unknown)"
      );
      return currentToken;
    } catch (error) {
      consecutiveFailures += 1;
      const backoff = RETRY_BACKOFFS_MS[
        Math.min(consecutiveFailures - 1, RETRY_BACKOFFS_MS.length - 1)
      ];
      console.warn(
        "[session-token] Refresh failed, retrying in",
        backoff,
        "ms",
        error
      );
      clearTimer();
      if (!disposed) {
        refreshTimer = setTimeout(() => {
          void runRefresh();
        }, backoff);
      }
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

if (typeof document !== "undefined") {
  // Beim Wiederkehren in den Tab: pruefen ob Refresh ueberfaellig ist
  // (setTimeout kann bei langer Tab-Sleep driften).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (!currentExp) return;
    const now = Date.now() / 1000;
    if (currentExp - REFRESH_BUFFER_SECONDS <= now) {
      void runRefresh();
    }
  });
}

// Refresh-Schleife nur im Draft-Modus starten.
if (mode === "draft" && currentToken) {
  scheduleNext();
}

/**
 * Aktueller Modus (`public` | `draft`).
 */
export function getMode() {
  return mode;
}

/**
 * Aktuell gueltiger Editor-Token (oder null im Public-Modus / fehlend).
 */
export function getCurrentToken() {
  return currentToken;
}

/**
 * Forciert einen sofortigen Refresh (z.B. nach einem 401 in einem API-Aufruf).
 * @returns {Promise<string|null>} neuer Token oder null bei Fehler.
 */
export function refreshNow() {
  return runRefresh();
}

/**
 * Registriert einen Subscriber, der bei jedem Token-Wechsel benachrichtigt wird.
 * @param {(token: string|null, reason: string) => void} subscriber
 * @returns {() => void} Unsubscribe-Funktion.
 */
export function onTokenChange(subscriber) {
  subscribers.add(subscriber);
  return () => subscribers.delete(subscriber);
}

/**
 * Aufraeumen (vor allem fuer Tests). Stoppt den Refresh-Loop.
 */
export function dispose() {
  disposed = true;
  clearTimer();
  subscribers.clear();
}
